import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import PropTypes from 'prop-types'
import { useNavigate, useParams } from 'react-router-dom'
import { StaticDateTimePicker } from '@mui/x-date-pickers'
import PrimaryButton from '../buttons/PrimaryButton'
import apiCalendar from '../../services/api/calendar'
import theme from '../../constants/theme'
import { CalendarPickerContainer, OverlayDiv } from '../styled'
import { months } from '../../constants/months'
import CalendarSquare from '../../assets/svgs/calendarSquare'
import TimeCircle from '../../assets/svgs/timeCircle'
import Monitor from '../../assets/svgs/Monitor'
import Play from '../../assets/svgs/Play'
import CalendarOutline from '../../assets/svgs/CalendarOutline'
import ClockOutline from '../../assets/svgs/ClockOutline'
import X from '../../assets/svgs/X'
import Mail from '../../assets/svgs/Mail'
import XCircle from '../../assets/svgs/XCircle'
import {
  InvitationCard,
  InvitationHeader,
  InvitationPill,
  InvitationTitle,
  InvitationSubtitle,
  InvitationBody,
  DateInfoRow,
  DateBox,
  DateBoxMonth,
  DateBoxDay,
  TimeInfoWrapper,
  TimeInfoRow,
  AvailableBadge,
  OutlinedButton,
  ActionButtonsRow,
} from './styled'

const DATE_VIEWS = ['day', 'month', 'year']

function PickerTabs({ view, onViewChange }) {
  const { t } = useTranslation()
  const isDate = DATE_VIEWS.includes(view)

  const tabStyle = (active) => ({
    flex: 1,
    padding: '10px 0',
    border: 'none',
    borderBottom: active
      ? `2px solid ${theme.talentinoBlue}`
      : '2px solid transparent',
    background: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: active ? theme.talentinoBlue : '#9CA3AF',
    transition: 'color 0.2s, border-color 0.2s',
  })

  return (
    <div
      style={{
        display: 'flex',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#fff',
      }}
    >
      <button style={tabStyle(isDate)} onClick={() => onViewChange('day')}>
        <CalendarOutline width={16} height={16} />
        {t('calendar.date')}
      </button>
      <button style={tabStyle(!isDate)} onClick={() => onViewChange('hours')}>
        <ClockOutline width={16} height={16} />
        {t('calendar.time')}
      </button>
    </div>
  )
}

function CalendarPicker({
  endDate,
  title,
  duration,
  extraMsg = '',
  companyName,
  positionName,
  type,
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n?.language
  const { token } = useParams()
  const navigate = useNavigate()

  const today = dayjs()
  const scheduleDuration = dayjs().add(duration, 'day')
  const maxDate = dayjs(endDate).set('hour', -1).startOf('hour')
  const [time, setTime] = useState(
    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  )
  const [localTime, setLocalTime] = useState(
    today?.format('YYYY-MM-DD HH:mm:ss') || '',
  )
  const [UTC, setUTC] = useState(today?.$d)

  const [isSchedule, setIsSchedule] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)

  const UpdateTime = () => {
    setTime(
      new Date().toLocaleString([], { hour: '2-digit', minute: '2-digit' }),
    )
  }
  setInterval(UpdateTime, 1000)

  function handleDateTime(e) {
    if (e) {
      const selectedDate = e.format('YYYY-MM-DD HH:mm:ss')
      setLocalTime(selectedDate)
      setUTC(e.$d)
      const isValidDate = e.isAfter(today) && e.isBefore(maxDate)
      setIsDisabled(!isValidDate)
    }
  }

  const handleDecline = () =>
    apiCalendar.declineInterview(token).then(() => navigate(0))

  const handleSubmit = () => {
    apiCalendar
      .patch(token, { localTime, utc: UTC })
      .then(({ interviewToken }) => navigate(`/interview/${interviewToken}`))
  }

  const handleStartNow = () => {
    handleDateTime(dayjs(today))
    handleSubmit(dayjs(today))
  }

  useEffect(() => {
    if (!localTime) {
      setIsDisabled(true)
    }
  }, [localTime])

  const monthLabel =
    months[today.$d.getMonth()]?.[locale] || months[today.$d.getMonth()]?.en
  const fullDate = today.format('dddd, D MMMM')

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {isSchedule && (
        <>
          <OverlayDiv onClick={() => setIsSchedule(false)} />
          <CalendarPickerContainer>
            {/* Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 20px 10px',
                borderBottom: '1px solid #f3f4f6',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  letterSpacing: '1.5px',
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                }}
              >
                {t('calendar.selectDateTime')}
              </span>
              <button
                onClick={() => setIsSchedule(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px',
                  borderRadius: '6px',
                }}
              >
                <X width={18} height={18} />
              </button>
            </div>

            {/* Picker — no Row wrapper to avoid Bootstrap negative margins */}
            <StaticDateTimePicker
              defaultValue={today}
              minDateTime={today}
              maxDateTime={
                duration && scheduleDuration.isBefore(maxDate)
                  ? scheduleDuration
                  : maxDate
              }
              disablePast
              onChange={(e) => handleDateTime(e)}
              slots={{ tabs: PickerTabs }}
              slotProps={{ actionBar: { actions: [] } }}
              sx={{
                width: '100%',

                // ── Toolbar background ──

                '& .MuiPickersToolbarText-root': {
                  fontWeight: '600',
                },
                '& .MuiPickersToolbarText-root.Mui-selected': {
                  color: theme.brandColorOrange,
                },

                '& .MuiDateTimePickerToolbar-ampmSelection .MuiPickersToolbarText-root':
                  {
                    fontSize: '13px',
                    padding: '2px 5px',
                    borderRadius: '4px',
                  },
                '& .MuiDateTimePickerToolbar-ampmSelection .MuiPickersToolbarText-root.Mui-selected':
                  {
                    color: `${theme.brandColorOrange} !important`,
                    backgroundColor: 'rgba(131, 131, 131, 0.18)',
                  },

                // ── Tabs ──
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.talentinoBlue,
                },
                '& .MuiTab-root.Mui-selected': {
                  color: theme.talentinoBlue,
                },

                // ── Calendar selected day ──
                '& .MuiPickersDay-root.Mui-selected': {
                  backgroundColor: '#076698 !important',
                  color: 'white !important',
                },
                '& .MuiPickersDay-root.Mui-selected:hover': {
                  backgroundColor: '#137FC3 !important',
                },
                '& .MuiPickersDay-root.Mui-selected:focus': {
                  backgroundColor: '#076698 !important',
                },

                // ── Clock ──
                '& .MuiClock-pin': { backgroundColor: '#076698' },
                '& .MuiClockPointer-root': { backgroundColor: '#076698' },
                '& .MuiClockPointer-thumb': {
                  borderColor: '#076698',
                  backgroundColor: '#076698',
                },
                '& .MuiClockNumber-root.Mui-selected': {
                  backgroundColor: '#076698',
                  color: 'white',
                },
              }}
            />

            {/* Action buttons */}
            <div
              style={{ padding: '4px 16px 20px', display: 'flex', gap: '10px' }}
            >
              <div style={{ flex: 1 }}>
                <PrimaryButton
                  width="100%"
                  backgroundcolor="transparent"
                  border={`1.5px solid ${theme.talentinoRed}`}
                  hovercolor={theme.talentinoRed}
                  color={theme.talentinoRed}
                  onClick={handleDecline}
                  disabled={false}
                >
                  {t('calendar.declineInvitation')}
                </PrimaryButton>
              </div>
              <div style={{ flex: 1 }}>
                <PrimaryButton
                  width="100%"
                  hovercolor={theme.talentinoBlue}
                  onClick={handleSubmit}
                  disabled={isDisabled}
                >
                  {t('general.submit')}
                </PrimaryButton>
              </div>
            </div>
          </CalendarPickerContainer>
        </>
      )}

      <InvitationCard>
        <InvitationHeader>
          <div>
            <InvitationPill>
              <Mail width={14} height={14} />
              {t('calendar.interviewInvitation')}
            </InvitationPill>
          </div>
          <InvitationTitle>
            {t('calendar.invitedToInterview', {
              position: positionName,
              company: companyName,
            })}
          </InvitationTitle>

          {title && <InvitationSubtitle>{title}</InvitationSubtitle>}
          {extraMsg && (
            <InvitationSubtitle style={{ marginTop: '4px' }}>
              {extraMsg}
            </InvitationSubtitle>
          )}
        </InvitationHeader>

        <InvitationBody>
          <DateInfoRow>
            <DateBox>
              <DateBoxMonth>{monthLabel}</DateBoxMonth>
              <DateBoxDay>{today.$d.getDate()}</DateBoxDay>
            </DateBox>
            <TimeInfoWrapper>
              <TimeInfoRow>
                <TimeCircle width="16" height="16" />
                <span>
                  {fullDate} &middot; {time}
                </span>
                <AvailableBadge>
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: '#16A34A',
                      display: 'inline-block',
                    }}
                  />
                  {t('calendar.availableNow') || 'Available now'}
                </AvailableBadge>
              </TimeInfoRow>
              <TimeInfoRow style={{ color: '#6B7280' }}>
                <Monitor width={16} height={16} color="#6B7280" />
                <span>
                  {type == 'TEXT'
                    ? t('calendar.textInterview')
                    : type == 'VIDEO'
                      ? t('calendar.videoInterview')
                      : type == 'COVERLETTER'
                        ? t('calendar.coverLetterInterview')
                        : type == 'FORM'
                          ? t('calendar.formInterview')
                          : t('calendar.inPersonInterview')}
                </span>
              </TimeInfoRow>
            </TimeInfoWrapper>
          </DateInfoRow>

          <PrimaryButton
            width="100%"
            style={{
              height: '44px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
            }}
            onClick={handleStartNow}
          >
            <Play width={14} height={14} color="white" />
            &nbsp;{t('calendar.startNow')}
          </PrimaryButton>

          <ActionButtonsRow>
            <OutlinedButton
              borderColor={theme.talentinoBlue}
              color={theme.talentinoBlue}
              onClick={() => setIsSchedule(true)}
            >
              <CalendarSquare width="14" height="14" color="#076698" />
              {t('calendar.scheduleButton')}
            </OutlinedButton>
            <OutlinedButton
              borderColor={theme.talentinoRed}
              color={theme.talentinoRed}
              onClick={handleDecline}
            >
              <XCircle width={13} height={13} />
              {t('calendar.declineInvitation')}
            </OutlinedButton>
          </ActionButtonsRow>
        </InvitationBody>
      </InvitationCard>
    </LocalizationProvider>
  )
}

PickerTabs.propTypes = {
  view: PropTypes.string.isRequired,
  onViewChange: PropTypes.func.isRequired,
}

CalendarPicker.propTypes = {
  endDate: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
  extraMsg: PropTypes.string,
  companyName: PropTypes.string,
  positionName: PropTypes.string,
  type: PropTypes.string,
}

export default CalendarPicker
